import { Box, Loader, Stepper } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { modals } from '@mantine/modals';
import { CreatorAgentSuccessFlowStepKey as AgentSuccessStep } from '@newo/tenant-metadata';
import { type FC, useCallback, useMemo } from 'react';
import Title from '@/components/Title';
import { useHostContext } from '@/context/HostContext.tsx';
import ActivateAccountInfoContent from '@/pages/AgentSuccess/components/ActivateAccountInfoContent';
import CollapseStepCard from '@/pages/AgentSuccess/components/StepsBlock/components/CollapseStepCard';
import StepCard from '@/pages/AgentSuccess/components/StepsBlock/components/StepCard';
import { useAgentSuccessActions } from '@/pages/AgentSuccess/components/StepsBlock/hooks/useAgentSuccessActions.tsx';
import { partnerMetadata } from '@/services/PartnerMetadata';
import useAppStore from '@/store/app.ts';
import { cn } from '@/utils';

type StepsBlockProps = {
	handleSetCardRef: (
		step: AgentSuccessStep,
		ref: HTMLDivElement | null
	) => void;
};

const StepsBlock: FC<StepsBlockProps> = ({ handleSetCardRef }) => {
	const config = useMemo(() => partnerMetadata.getAgentSuccessConfig(), []);
	const isCollapseVariant = useMemo(
		() => partnerMetadata.isAgentSuccessCollapseVariant(),
		[]
	);

	const {
		handleActionClick,
		stepperActiveStep,
		expandedStep,
		handleExpand,
		handleStepClick,
		isRedirecting
	} = useAgentSuccessActions(config);

	const { isMiniCreatorMode, localization } = useHostContext();

	const agentSuccessActiveStep = useAppStore(
		(state) => state.agentSuccessActiveStep
	);
	const isMobile = useMediaQuery('(max-width: 768px)');

	const openActiveInfoModal = useCallback(
		(key: AgentSuccessStep) => {
			if (key !== AgentSuccessStep.ActivateAccount) {
				return;
			}

			modals.open({
				fullScreen: isMobile && !isMiniCreatorMode,
				centered: !isMobile && !isMiniCreatorMode,
				radius: isMobile || isMiniCreatorMode ? 0 : '24',
				size: isMobile || isMiniCreatorMode ? '100%' : 'lg',
				lockScroll: !isMiniCreatorMode,
				styles: {
					root: { '--mb-z-index': 1004 },
					inner: { '--mb-z-index': 1004 },
					overlay: { '--mb-z-index': 1004 }
				},
				classNames: {
					overlay: cn({ 'absolute inset-0': isMiniCreatorMode }),
					inner: cn('!p-0', {
						'h-full absolute inset-0': isMiniCreatorMode
					}),
					body: cn('!px-[32px] !h-full flex flex-col flex-1', {
						'!px-2': isMiniCreatorMode
					}),
					header: cn('flex !items-start !p-[32px]', {
						'!py-0 !px-2 !min-h-fit': isMiniCreatorMode
					}),
					content: 'h-full max-h-full w-full overflow-hidden flex flex-col'
				},
				overlayProps: { opacity: 0.3, blur: 3 },
				title: (
					<span
						className={cn('intro-title text-[28px] font-semibold', {
							'text-base': isMiniCreatorMode
						})}
					>
						{localization.translate(
							'creator',
							'agent_success.activate_account_info_modal.title'
						)}
					</span>
				),
				children: (
					<ActivateAccountInfoContent
						rich={localization.rich}
						translate={localization.translate}
					/>
				),
				...(isMiniCreatorMode
					? {
							portalProps: {
								target: '#mini-creator-container'
							}
						}
					: {})
			});
		},
		[isMobile, isMiniCreatorMode, localization]
	);

	if (isRedirecting) {
		return (
			<div className="flex items-center justify-center py-20">
				<Loader />
			</div>
		);
	}

	return (
		<div
			className={cn('flex flex-col gap-5 sm:gap-8', {
				'!gap-2': isMiniCreatorMode
			})}
		>
			<Title
				className="text-primary-900 w-full !text-base !text-left ![font-family:var(--font-headlines)] font-semibold !m-0 !p-0 !leading-[28px]"
				level={4}
				size="h5"
			>
				{localization.translate('creator', 'agent_success.next_steps.label')}
			</Title>

			{config.isStepperIncluded ? (
				<Stepper
					active={stepperActiveStep}
					onStepClick={handleStepClick}
					size={isMiniCreatorMode ? 'xs' : 'lg'}
				>
					{config.stepOrder.map((key) => (
						<Stepper.Step key={key} />
					))}
				</Stepper>
			) : undefined}
			{config.stepOrder.map((key) => {
				const step = config.steps[key];
				if (!step) {
					return null;
				}

				return (
					<Box
						className={cn('pb-5', {
							'pb-2': isMiniCreatorMode
						})}
						key={key}
						ref={(ref) => {
							handleSetCardRef(key, ref);
						}}
					>
						{isCollapseVariant ? (
							<CollapseStepCard
								cardData={step}
								handleActionClick={handleActionClick}
								handleExpand={handleExpand}
								isExpanded={expandedStep === key}
								isMiniCreatorMode={isMiniCreatorMode}
								isSelected={agentSuccessActiveStep === key}
								onInfoIconClick={openActiveInfoModal}
								step={key}
								translate={localization.translate}
							/>
						) : (
							<StepCard
								cardData={step}
								handleActionClick={handleActionClick}
								isMiniCreatorMode={isMiniCreatorMode}
								isSelected={agentSuccessActiveStep === key}
								onInfoIconClick={openActiveInfoModal}
								step={key}
								translate={localization.translate}
							/>
						)}
					</Box>
				);
			})}
		</div>
	);
};

export default StepsBlock;
